import React, { useCallback, useMemo } from "react";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/es/form";
import { useIntl } from "react-intl";

import { Button, Dropdown, Popconfirm, useModal } from "@mapmycustomers/ui";

import styles from "../../CreateEditActivityModal.module.scss";
import { RecurringActionType } from "../../enum/RecurringActionType";
import FormValues from "../../types/FormValues";
import SaveOption from "../../types/SaveOption";

import messages from "./messages";

interface Props {
  buttonTitle: string;
  disabled?: boolean;
  doesActivityMeetCriteria?: boolean;
  form?: FormInstance<FormValues>;
  isActivityPublic: boolean;
  isAssigneeNotCreator?: boolean;
  isRecurChanged?: boolean;
  loading: boolean;
  onCreate?: () => void;
  saveOptions?: SaveOption[];
  showCheckInSubmit?: boolean;
}

const SubmitButton = ({
  buttonTitle,
  disabled,
  doesActivityMeetCriteria = true,
  form,
  isActivityPublic,
  isAssigneeNotCreator,
  isRecurChanged,
  loading,
  onCreate,
  saveOptions,
  showCheckInSubmit,
}: Props) => {
  const intl = useIntl();
  const [modal, contextHolder] = useModal();

  const handleSubmit = useCallback(() => {
    if (disabled) {
      return;
    }

    if (isActivityPublic) {
      modal.show({
        cancelButtonProps: {
          type: "text",
        },
        cancelText: intl.formatMessage(messages.publicConfirmationCancel),
        className: styles.confirmationModal,
        content: intl.formatMessage(messages.publicConfirmationContent),
        okText: intl.formatMessage(messages.publicConfirmationOk),
        onOk: onCreate,
        title: intl.formatMessage(messages.publicConfirmationTitle),
        type: "confirm",
      });
    } else {
      onCreate?.();
    }
  }, [disabled, intl, isActivityPublic, modal, onCreate]);

  const handleMenuItemClick = useCallback(
    (updateType?: RecurringActionType) => () => {
      form?.setFieldsValue({ updateType });
      if (isRecurChanged) {
        return;
      }
      handleSubmit();
    },
    [form, handleSubmit, isRecurChanged]
  );

  const withRecurConfirm = useCallback(
    (children: React.ReactNode) => (
      <Popconfirm
        cancelText={intl.formatMessage(messages.recurConfirmationCancel)}
        description={intl.formatMessage(messages.recurConfirmationTitle)}
        disabled={!isRecurChanged}
        okText={intl.formatMessage(messages.recurConfirmationOk)}
        onConfirm={handleSubmit}
        placement="rightTop"
      >
        {children}
      </Popconfirm>
    ),
    [handleSubmit, intl, isRecurChanged]
  );

  const withFrequencyCriteriaConfirm = useCallback(
    (children: React.ReactNode) => (
      <Popconfirm
        cancelText={intl.formatMessage(messages.frequencyCriteriaConfirmationCancel)}
        description={intl.formatMessage(messages.frequencyCriteriaConfirmationTitle)}
        okText={intl.formatMessage(messages.frequencyCriteriaConfirmationOk)}
        onConfirm={handleSubmit}
        placement="rightTop"
      >
        {children}
      </Popconfirm>
    ),
    [handleSubmit, intl]
  );

  const submitButton = useMemo(() => {
    if (showCheckInSubmit) {
      return (
        <Button
          disabled={disabled || loading}
          loading={loading}
          onClick={handleSubmit}
          type="primary"
        >
          {buttonTitle}
        </Button>
      );
    }
    if (!doesActivityMeetCriteria) {
      return withFrequencyCriteriaConfirm(
        <Button disabled={disabled || loading} loading={loading} type="primary">
          {buttonTitle}
        </Button>
      );
    }
    if (saveOptions?.length) {
      return (
        <Dropdown
          menu={{
            items: [
              {
                children: [
                  ...(saveOptions.includes(SaveOption.JUST_THIS_ACTIVITY)
                    ? [
                        {
                          key: "just-this",
                          label: isRecurChanged
                            ? withRecurConfirm(intl.formatMessage(messages.updateMenuOnlyThis))
                            : intl.formatMessage(messages.updateMenuOnlyThis),
                          onClick: handleMenuItemClick(),
                        },
                      ]
                    : []),
                  ...(saveOptions.includes(SaveOption.THIS_AND_FEATURE_ACTIVITIES)
                    ? [
                        {
                          key: "this-and-future",
                          label: isRecurChanged
                            ? withRecurConfirm(intl.formatMessage(messages.updateMenuThisAndFuture))
                            : intl.formatMessage(messages.updateMenuThisAndFuture),
                          onClick: handleMenuItemClick(RecurringActionType.Future),
                        },
                      ]
                    : []),
                  ...(saveOptions.includes(SaveOption.ALL_ACTIVITIES)
                    ? [
                        {
                          key: "all",
                          label: isRecurChanged
                            ? withRecurConfirm(intl.formatMessage(messages.updateMenuAll))
                            : intl.formatMessage(messages.updateMenuAll),
                          onClick: handleMenuItemClick(RecurringActionType.All),
                        },
                      ]
                    : []),
                ],
                label: isAssigneeNotCreator
                  ? intl.formatMessage(messages.updateMenuAssigneeNotCreatorTitle)
                  : intl.formatMessage(messages.updateMenuTitle),
                type: "group",
              },
            ],
          }}
          overlayClassName={styles.actionDropdown}
          trigger={["click"]}
        >
          <Button
            disabled={disabled || loading}
            loading={loading}
            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
            type="primary"
          >
            {buttonTitle}
          </Button>
        </Dropdown>
      );
    }
    return isRecurChanged ? (
      withRecurConfirm(
        <Button disabled={disabled || loading} loading={loading} type="primary">
          {buttonTitle}
        </Button>
      )
    ) : (
      <Button
        disabled={disabled || loading}
        loading={loading}
        onClick={handleSubmit}
        type="primary"
      >
        {buttonTitle}
      </Button>
    );
  }, [
    buttonTitle,
    disabled,
    doesActivityMeetCriteria,
    handleMenuItemClick,
    handleSubmit,
    intl,
    isAssigneeNotCreator,
    isRecurChanged,
    loading,
    saveOptions,
    showCheckInSubmit,
    withFrequencyCriteriaConfirm,
    withRecurConfirm,
  ]);

  return (
    <>
      {submitButton}
      {contextHolder}
    </>
  );
};

export default SubmitButton;
